<template>
  <!-- <div class="menu-products"></div> -->
</template>

<script>
import { keyMapsMutation, keyMapsAction } from "../helpers/storeKeys";
import LocalStorageFunc from "../helpers/localStorageFunc";

export default {
  name: "MenuProducts",
  props: ["dataResults", "moduleProps", "productCategory"],
  mounted() {
    const nxg = JSON.parse(this.dataResults);

    const convertedProductItems = this.convertNxgToProductItems(nxg);

    const addOnItemDefs = this.buildAddOnItemDefsFromNxg(
      nxg?.addons || [],
      nxg?.addOnGroupDefinitions || [],
    );

    const toppingItems = this.buildToppingItemsFromProductItems(
      convertedProductItems,
      addOnItemDefs,
    );

    this.$store.commit(keyMapsMutation.UPDATE_PIZZA_TOPPINGS, toppingItems);
    this.$store.commit(
      keyMapsMutation.UPDATE_PRODUCT_ITEMS,
      convertedProductItems,
    );
    this.$store.commit(
      keyMapsMutation.UPDATE_CART_FROM_LOCAL_STORAGE,
      convertedProductItems,
    );
  },
  methods: {
    getCurrentOrderType() {
      const deliveryType = LocalStorageFunc.getDeliveryType();

      const t = String(deliveryType).toUpperCase().trim();

      if (t === "D") return "DELIVERY";
      if (t === "P") return "PICKUP";
      if (t === "DINEIN") return "DINE_IN";

      return null;
    },

    isOrderTypeDisabled(disabledOrderTypes = []) {
      if (!Array.isArray(disabledOrderTypes) || !disabledOrderTypes.length) {
        return false;
      }

      const currentType = this.getCurrentOrderType();
      if (!currentType) return false;

      const normalized = disabledOrderTypes.map((x) =>
        String(x).toUpperCase().trim(),
      );
      return normalized.includes(currentType);
    },

    addIfPresent(obj, key, value) {
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === "string" && value.trim() === "") return;
      if (typeof value === "object" && Object.keys(value).length === 0) return;
      obj[key] = value;
    },
    convertNxgToProductItems(nxgData) {
      const productItems = {};

      (nxgData?.productDefinitions || []).forEach((prod) => {
        if (this.isOrderTypeDisabled(prod.disabledOrderTypes || [])) {
          return;
        }

        // Variant-level filtering for this product
        const enabledVariants = (prod.variants || []).filter(
          (v) => !this.isOrderTypeDisabled(v.disabledOrderTypes || []),
        );

        // If no variant is valid for this order type, skip product
        if (!enabledVariants.length) {
          return;
        }

        (prod.categoryInfo || []).forEach((cat) => {
          const catName = cat.categoryName || "Others";
          if (!productItems[catName]) {
            productItems[catName] = {
              categoryName: catName,
              categoryId: this.toInt(cat.metadataMap?.categoryId?.values?.[0]),
              categoryType: this.toInt(
                cat.metadataMap?.categoryType?.values?.[0],
              ),
              data: {},
              displayCategory: true,
              showCategory: false,
            };
          }

          let productCode =
            prod.code ||
            prod.metadataMap?.menuCode?.values?.[0] ||
            prod.menuCode ||
            String(prod.parentSkuId);

          const defaultVariant = this.pickDefaultVariant(prod.variants || []);
          productCode = defaultVariant?.skuId || productCode;
          const defaultToppings = this.buildDefaultToppings(
            prod,
            nxgData?.addOnGroupDefinitions,
            nxgData?.addons,
          );
          const allowedToppings = this.buildAllowedToppings(
            prod,
            nxgData?.addOnGroupDefinitions,
            nxgData?.addons,
          );
          const toppingsForPriceMap = [
            ...new Set([
              ...(defaultToppings || []),
              ...(allowedToppings || []),
            ]),
          ];

          const productObj = {};
          this.addIfPresent(productObj, "id", this.toInt(prod.parentSkuId));
          this.addIfPresent(productObj, "description", prod.description);
          this.addIfPresent(productObj, "name", prod.displayName || prod.name);
          this.addIfPresent(
            productObj,
            "image",
            this.extractImage(prod.mediaList),
          );
          this.addIfPresent(
            productObj,
            "defaultPrice",
            defaultVariant?.price?.regularPrice,
          );
          this.addIfPresent(
            productObj,
            "originalPrice",
            defaultVariant?.price?.regularPrice,
          );
          this.addIfPresent(
            productObj,
            "itemPrice",
            defaultVariant?.price?.currentPrice,
          );
          this.addIfPresent(
            productObj,
            "priceWithoutTax",
            defaultVariant?.price?.currentPrice,
          );

          const crusts = this.buildCrustsGrouped(prod, toppingsForPriceMap);
          if (crusts.length) this.addIfPresent(productObj, "crust", crusts);

          this.addIfPresent(productObj, "defaultToppings", defaultToppings);
          this.addIfPresent(
            productObj,
            "selectedSizeId",
            this.toInt(this.getSelectedSizeIdFromVariants(prod.variants || [])),
          );
          this.addIfPresent(
            productObj,
            "selectedCrustId",
            String(this.getSelectedCrustIdFromVariants(prod.variants || [])),
          );
          this.addIfPresent(
            productObj,
            "isCustomizable",
            this.truthy((prod.metadataMap?.isCustomizable?.values || [])[0]),
          );
          this.addIfPresent(
            productObj,
            "isReplaceable",
            this.truthy((prod.metadataMap?.isReplaceable?.values || [])[0]),
          );
          this.addIfPresent(productObj, "allowedToppings", allowedToppings);

          const foodLabel = (prod.metadataMap?.foodLabel?.values || [])[0];
          this.addIfPresent(
            productObj,
            "productType",
            foodLabel?.toLowerCase() === "veg" ? 0 : 1,
          );

          this.addIfPresent(
            productObj,
            "defaultCrustPrice",
            defaultVariant?.price?.currentPrice,
          );
          this.addIfPresent(productObj, "code", prod.menuCode);
          this.addIfPresent(productObj, "sizeCode", prod.sizeCode);
          this.addIfPresent(productObj, "sortOrder", prod.sortOrder);
          this.addIfPresent(productObj, "buttonText", "ADD TO CART");
          const taxes = this.buildTaxes(prod.taxation);
          this.addIfPresent(productObj, "taxes", taxes);

          const images = this.buildImages(prod.mediaList);
          if (images.length) this.addIfPresent(productObj, "images", images);

          this.addIfPresent(
            productObj,
            "menuCategoryCode",
            (prod.metadataMap?.menuCategoryCode?.values || [])[0],
          );
          this.addIfPresent(
            productObj,
            "filters",
            prod.metadataMap?.filterKeys?.values,
          );
          this.addIfPresent(
            productObj,
            "productModel",
            (prod.metadataMap?.productModel?.values || [])[0],
          );
          this.addIfPresent(
            productObj,
            "cartImage",
            this.extractCartImage(prod.mediaList),
          );
          this.addIfPresent(
            productObj,
            "taxPercentage",
            this.toFloat(prod.taxation?.[0]?.taxes?.[0]?.value),
          );

          const ratings = this.buildRatings(prod.ratingAndSubCount);
          if (ratings && Object.values(ratings).some((x) => x !== 0)) {
            this.addIfPresent(productObj, "ratings", ratings);
          }
          this.addIfPresent(
            productObj,
            "productName",
            prod.displayName || prod.name,
          );
          this.addIfPresent(productObj, "skuId", String(prod.parentSkuId));

          this.addIfPresent(
            productObj,
            "customCategoryId",
            this.toInt(
              cat.metadataMap?.categoryId?.values?.[0] || cat.categoryId,
            ),
          );
          this.addIfPresent(productObj, "customCategoryName", cat.categoryName);

          this.addIfPresent(
            productObj,
            "defaultCrustName",
            this.getFirstAttributeValueName(prod, "Crust"),
          );
          this.addIfPresent(
            productObj,
            "crustName",
            this.getFirstAttributeValueName(prod, "Crust"),
          );
          this.addIfPresent(
            productObj,
            "crustId",
            String(this.getFirstAttributeValueMeta(prod, "Crust", "crustId")),
          );
          this.addIfPresent(
            productObj,
            "defaultSizeName",
            this.getFirstAttributeValueName(prod, "Size"),
          );
          this.addIfPresent(
            productObj,
            "sizeName",
            this.getFirstAttributeValueName(prod, "Size"),
          );
          this.addIfPresent(
            productObj,
            "sizeId",
            this.toInt(this.getFirstAttributeValueMeta(prod, "Size", "sizeId")),
          );
          this.addIfPresent(productObj, "product", {
            id: this.toInt(prod.parentSkuId),
            code: prod.menuCode || "",
          });

          productObj.isUserCustomizable = false; // keep default

          productObj.addTopngs = [];
          productObj.remTopngs = [];
          productObj.repTopngs = [];

          productItems[catName].data[productCode] = productObj;
        });
      });

      (nxgData?.comboDefinitions || []).forEach((combo) => {
        if (this.isOrderTypeDisabled(combo?.disabledOrderTypes || [])) {
          return;
        }

        const isDynamicCombo = (combo?.groups || []).some(
          (group) => Array.isArray(group?.items) && group?.items.length > 1,
        );

        if (isDynamicCombo) {
          return;
        }

        const categories =
          combo?.categoryInfo && combo?.categoryInfo.length > 0
            ? combo?.categoryInfo
            : [{ categoryName: "Combos" }];

        categories.forEach((cat) => {
          const catName = cat?.categoryName || "Combos";

          if (!productItems[catName]) {
            productItems[catName] = {
              categoryName: catName,
              categoryId: this.toInt(
                cat?.metadataMap?.categoryId?.values?.[0] || cat?.categoryId,
              ),
              categoryType: this.toInt(
                cat?.metadataMap?.categoryType?.values?.[0] ||
                  cat?.categoryType,
              ),
              data: {},
              displayCategory: true,
              showCategory: false,
            };
          }

          const comboCode =
            combo?.metadataMap?.menuCode?.values?.[0] ||
            combo?.menuCode ||
            String(combo?.skuId);

          const comboObj = {};

          this.addIfPresent(comboObj, "id", this.toInt(combo?.skuId));
          this.addIfPresent(comboObj, "description", combo?.description);
          this.addIfPresent(
            comboObj,
            "name",
            combo?.displayName || combo?.comboName,
          );
          this.addIfPresent(
            comboObj,
            "image",
            this.extractImage(combo?.mediaList),
          );

          this.addIfPresent(
            comboObj,
            "defaultPrice",
            combo?.price?.regularPrice,
          );
          this.addIfPresent(
            comboObj,
            "originalPrice",
            combo?.price?.regularPrice,
          );
          this.addIfPresent(comboObj, "itemPrice", combo?.price?.currentPrice);
          this.addIfPresent(
            comboObj,
            "priceWithoutTax",
            combo?.price?.currentPrice,
          );

          const foodLabel = (combo?.metadataMap?.foodLabel?.values || [])[0];
          this.addIfPresent(
            comboObj,
            "productType",
            foodLabel?.toLowerCase() === "veg" ? 0 : 1,
          );

          this.addIfPresent(comboObj, "code", combo?.menuCode);
          this.addIfPresent(comboObj, "sizeCode", combo?.sizeCode);
          this.addIfPresent(comboObj, "buttonText", "ADD TO CART");

          this.addIfPresent(
            comboObj,
            "productModel",
            (combo?.metadataMap?.productModel?.values || [])[0],
          );
          this.addIfPresent(
            comboObj,
            "filters",
            combo?.metadataMap?.filterKeys?.values,
          );
          this.addIfPresent(
            comboObj,
            "menuCategoryCode",
            (combo?.metadataMap?.menuCategoryCode?.values || [])[0],
          );

          const images = this.buildImages(combo?.mediaList);
          if (images?.length) this.addIfPresent(comboObj, "images", images);

          this.addIfPresent(
            comboObj,
            "cartImage",
            this.extractCartImage(combo?.mediaList),
          );
          this.addIfPresent(
            comboObj,
            "productName",
            combo?.displayName || combo?.comboName,
          );
          this.addIfPresent(comboObj, "skuId", String(combo?.skuId));

          this.addIfPresent(comboObj, "product", {
            id: this.toInt(combo?.skuId),
            code: combo?.menuCode || "",
          });

          this.addIfPresent(comboObj, "isCombo", true);
          this.addIfPresent(comboObj, "comboUID", combo?.comboUID);
          this.addIfPresent(comboObj, "groups", combo?.groups);
          this.addIfPresent(comboObj, "isCartDisabled", combo?.isCartDisabled);
          this.addIfPresent(
            comboObj,
            "comboAmountType",
            combo?.comboAmountType,
          );

          const hasGroups =
            Array.isArray(combo?.groups) && combo?.groups.length > 0;
          // Static combos cannot be customized, hence false.
          this.addIfPresent(comboObj, "isCustomizable", false);
          comboObj.isUserCustomizable = false;

          this.addIfPresent(
            comboObj,
            "serves",
            (combo?.metadataMap?.serves?.values || [])[0],
          );

          this.addIfPresent(
            comboObj,
            "customCategoryId",
            this.toInt(
              cat?.metadataMap?.categoryId?.values?.[0] || cat?.categoryId,
            ),
          );
          this.addIfPresent(comboObj, "customCategoryName", cat?.categoryName);

          comboObj.addTopngs = [];
          comboObj.remTopngs = [];
          comboObj.repTopngs = [];

          productItems[catName].data[comboCode] = comboObj;
        });
      });

      return productItems;
    },

    // ========== helpers ==========
    buildCrustsGrouped(prod, toppingIdsForPrice) {
      const byCrust = new Map();
      (prod.variants || []).forEach((v) => {
        if (this.isOrderTypeDisabled(v.disabledOrderTypes || [])) {
          return;
        }

        const crustAttr = (v.attributes || []).find(
          (a) => (a?.name || "").toLowerCase() === "crust",
        );
        const sizeAttr = (v.attributes || []).find(
          (a) => (a?.name || "").toLowerCase() === "size",
        );
        if (!crustAttr || !sizeAttr) return;

        const crustId = (crustAttr.metadataMap?.crustId?.values || [])[0];
        const crustCode = (crustAttr.metadataMap?.code?.values || [])[0];
        const crustName =
          crustAttr.value || crustAttr.displayName || crustAttr.name;
        const crustDesc = crustAttr.description;

        const sizeId = (sizeAttr.metadataMap?.sizeId?.values || [])[0];
        const serves = (sizeAttr.metadataMap?.serves?.values || [])[0] || "1";
        const sizeName =
          sizeAttr.value || sizeAttr.displayName || sizeAttr.name;
        const sizeDesc = sizeAttr.description || `Serves ${serves}`;

        const priceNow = v.price?.currentPrice ?? v.price?.regularPrice;
        const priceMrp = v.price?.regularPrice ?? priceNow;
        const skuId = `${sizeId}#${crustId}#${prod.parentSkuId}`;

        const uniqGeneratedToppingsPriceId = {};
        (toppingIdsForPrice || []).forEach((tId) => {
          uniqGeneratedToppingsPriceId[tId] = `${tId}#${sizeId}`;
        });

        if (!byCrust.has(crustId)) {
          byCrust.set(crustId, {
            meta: { crustId, crustCode, crustName, crustDesc },
            sizes: [],
          });
        }
        byCrust.get(crustId).sizes.push({
          price: priceNow,
          description: sizeDesc,
          name: sizeName,
          sizeId: this.toInt(sizeId),
          code: (v.metadataMap?.code?.values || [])[0],
          uniqGeneratedPriceId: skuId,
          uniqGeneratedToppingsPriceId,
          serves: this.toInt(serves, 1),
          originalPrice: priceMrp,
          priceWithoutTax: priceNow,
          skuId,
          metadataMap: v.metadataMap || {},
        });
      });

      const crusts = [];
      for (const [, { meta, sizes }] of byCrust) {
        crusts.push({
          sizes,
          crustId: this.toInt(meta.crustId),
          description: meta.crustDesc,
          name: meta.crustName,
          code: meta.crustCode,
        });
      }
      return crusts;
    },

    buildDefaultToppings(variant, addOnGroupDefs, addOnList) {
      if (!variant) return [];

      const groupId = variant.defaultAddOnGroup?.[0]?.groupId;
      const group = (addOnGroupDefs || []).find((g) => g.groupId === groupId);
      if (!group) return [];

      return (group.addons || [])
        .map((a) => {
          const topping = (addOnList || []).find(
            (item) => item.skuId === a?.skuId,
          );
          return topping?.metadataMap?.toppingId?.values?.[0];
        })
        .filter(Boolean)
        .map((t) => this.toInt(t));
    },

    buildAllowedToppings(prod, addOnGroupDefs, addOnList) {
      const firstVariant = (prod.variants || [])[0];
      const ids = firstVariant?.addOnGroups || [];
      const out = [];
      (ids || []).forEach((gid) => {
        const g = (addOnGroupDefs || []).find(
          (x) => x.groupId === gid || x.groupId === gid?.groupId,
        );
        (g?.addons || []).forEach((a) => {
          const topping = (addOnList || []).find(
            (item) => item.skuId === a?.skuId,
          );
          if (topping)
            out.push(this.toInt(topping.metadataMap?.toppingId?.values?.[0]));
        });
      });
      return [...new Set(out)];
    },
    extractImage(mediaList = []) {
      // if (Array.isArray(mediaList) || mediaList.length === 0) {
      //   return "";
      // }

      const getFirstUrlFromMedia = (mediaItem) => {
        if (!mediaItem || !mediaItem.mediaUrls) return "";

        const urlKeys = Object.keys(mediaItem.mediaUrls || {});
        const firstKeyWithValue = urlKeys.find(
          (key) => mediaItem.mediaUrls[key],
        );

        return firstKeyWithValue ? mediaItem.mediaUrls[firstKeyWithValue] : "";
      };

      const defaultMediaItem = mediaList.find((item) => item.default);
      const defaultUrl = getFirstUrlFromMedia(defaultMediaItem);
      if (defaultUrl) return defaultUrl;

      for (const mediaItem of mediaList) {
        const url = getFirstUrlFromMedia(mediaItem);
        if (url) return url;
      }

      return "";
    },

    extractCartImage(mediaList) {
      return mediaList?.find((m) => m.mediaUrls?.cart)?.mediaUrls?.cart;
    },
    buildImages(mediaList = []) {
      const list = [];
      mediaList.forEach((m) => {
        const urls = m?.mediaUrls || {};
        Object.keys(urls).forEach((key) => {
          list.push({ mediaPath: urls[key], mediaSize: key });
        });
      });
      return list;
    },
    buildTaxes(taxation = []) {
      if (!taxation.length) return {};
      const out = {};
      (taxation[0].taxes || []).forEach((t) => {
        if (t?.name) out[t.name] = String(t.value ?? "");
      });
      return out;
    },
    buildRatings(r) {
      if (!r) return null;
      const out = {
        strRtgSub: 0,
        city: 0,
        ctRtgSub: 0,
        global: 0,
        gblRtgSub: 0,
      };
      const firstPair = (obj) => {
        if (!obj) return [0, 0];
        const k = Object.keys(obj)[0];
        return [this.toFloat(k, 0), this.toInt(obj[k], 0)];
      };
      const [gRating, gSub] = firstPair(r.global);
      out.global = gRating;
      out.gblRtgSub = gSub;
      const [cRating, cSub] = firstPair(r.city);
      out.city = cRating;
      out.ctRtgSub = cSub;
      const [sRating, sSub] = firstPair(r.store);
      out.strRtgSub = sSub || 0;
      return out;
    },

    pickDefaultVariant(variants) {
      if (!Array.isArray(variants) || !variants.length) return null;
      return variants.find((v) => v.defaultVariant) || variants[0];
    },
    getSelectedCrustIdFromVariants(variants) {
      const def = this.pickDefaultVariant(variants);
      const crustAttr = (def?.attributes || []).find(
        (a) => (a?.name || "").toLowerCase() === "crust",
      );
      return (crustAttr?.metadataMap?.crustId?.values || [])[0];
    },
    getSelectedSizeIdFromVariants(variants) {
      const def = this.pickDefaultVariant(variants);
      const sizeAttr = (def?.attributes || []).find(
        (a) => (a?.name || "").toLowerCase() === "size",
      );
      return (sizeAttr?.metadataMap?.sizeId?.values || [])[0];
    },
    getFirstAttributeValueName(prod, attrName) {
      if (!prod || !Array.isArray(prod.variants)) return null;

      //Find default variant (fallback to first)
      const defaultVariant =
        prod.variants.find((v) => v.defaultVariant) || prod.variants[0];

      if (!defaultVariant) return null;

      const attr = (defaultVariant.attributes || []).find(
        (a) => (a?.name || "").toLowerCase() === attrName.toLowerCase(),
      );

      if (!attr) return null;

      return attr.value || attr.displayName || attr.name || null;
    },
    getFirstAttributeValueMeta(prod, attrName, metaKey) {
      const a = (prod.attributes || []).find(
        (x) => (x?.name || "").toLowerCase() === attrName.toLowerCase(),
      );
      const v = (a?.values || [])[0];
      return (v?.metadataMap?.[metaKey]?.values || [])[0];
    },

    toInt(x, d) {
      const n = parseInt(x, 10);
      return Number.isFinite(n) ? n : d;
    },
    toFloat(x, d) {
      const n = parseFloat(x);
      return Number.isFinite(n) ? n : d;
    },
    truthy(x) {
      if (typeof x === "boolean") return x;
      if (typeof x === "string") return x.toLowerCase() === "true";
      return !!x;
    },

    buildAddOnItemDefsFromNxg(addons = [], addOnGroupDefs = []) {
      const defs = {};

      const addOnByItemId = {};
      addons.forEach((addon) => {
        if (addon.itemId) {
          addOnByItemId[addon.itemId] = addon;
        }
      });

      const ensureDefForAddOnItem = (addOnItem) => {
        const toppingId = this.toInt(
          (addOnItem.metadataMap?.toppingId?.values || [])[0] ||
            addOnItem.toppingId,
        );
        if (!toppingId) return null;

        if (!defs[toppingId]) {
          const foodLabel = (addOnItem.metadataMap?.foodLabel?.values || [])[0];
          const toppingCategory = (addOnItem.metadataMap?.toppingCategory
            ?.values || [])[0];
          const numericType = this.toInt(
            (addOnItem.metadataMap?.toppingType?.values || [])[0],
            null,
          );

          const { type, typeName } = this.getToppingTypeAndName({
            numericType,
            foodLabel,
            toppingCategory,
          });

          defs[toppingId] = {
            toppingId,
            name: addOnItem.displayName || addOnItem.name,
            description: addOnItem.description,
            images: this.extractImage(addOnItem.mediaList),
            type,
            typeName,
            custAttrField:
              (addOnItem.metadataMap?.custAttrField?.values || [])[0] || "",
            price: {},
            priceWithoutTax: {},
          };
        }

        return defs[toppingId];
      };

      (addOnGroupDefs || []).forEach((group) => {
        (group.addons || []).forEach((gAddon) => {
          const addOnItem = addOnByItemId[gAddon.itemId];
          if (!addOnItem) return;

          const def = ensureDefForAddOnItem(addOnItem);
          if (!def) return;

          const skuId = gAddon.skuId || "";
          const parts = String(skuId).split("#");
          const rawSizeId = parts.length > 1 ? parts[1] : null;
          const sizeId = this.toInt(rawSizeId);
          if (!sizeId) return;

          const priceObj = gAddon.price || {};
          const priceNow =
            priceObj.currentPrice ??
            priceObj.regularPrice ??
            this.toFloat(priceObj, null);

          if (priceNow == null) return;

          const key = String(sizeId);

          if (def.price[key] == null) {
            def.price[key] = String(priceNow);
          }
          if (def.priceWithoutTax[key] == null) {
            def.priceWithoutTax[key] = String(priceNow);
          }
        });
      });

      return defs;
    },

    getToppingTypeAndName({ numericType, foodLabel, toppingCategory }) {
      const cat = (toppingCategory || "").toUpperCase();
      const label = (foodLabel || "").toLowerCase();

      if (numericType === 2 || cat === "CHEESE") {
        return { type: 2, typeName: "CHEESE" };
      }

      if (
        numericType === 1 ||
        ["nonveg", "non-veg", "non veg"].includes(label)
      ) {
        return { type: 1, typeName: "NONVEG" };
      }

      return { type: 0, typeName: "VEG" };
    },

    buildToppingItemsFromProductItems(productItems = {}, addOnDefs = {}) {
      const toppingSizeIds = {};

      Object.values(productItems || {}).forEach((cat) => {
        Object.values(cat.data || {}).forEach((prod) => {
          const allToppings = [
            ...(prod.allowedToppings || []),
            ...(prod.defaultToppings || []),
          ];

          const sizeId =
            prod.sizeId ||
            prod.selectedSizeId ||
            this.toInt(
              prod.product?.sizeId || prod.defaultSizeId || prod.sizeId,
            );

          allToppings.forEach((tId) => {
            if (!toppingSizeIds[tId]) toppingSizeIds[tId] = new Set();
            if (sizeId) toppingSizeIds[tId].add(String(sizeId));
          });
        });
      });

      const toppingItems = [];

      Object.keys(addOnDefs || {}).forEach((tIdStr) => {
        const toppingId = this.toInt(tIdStr);
        const def = addOnDefs[tIdStr];
        if (!def) return;

        // const sizeSet =
        //   toppingSizeIds[toppingId] || new Set(Object.keys(def.price || {})); //old implementation

        // Merge sizes found in products AND sizes found in the price list
        const productSizes = toppingSizeIds[toppingId]
          ? [...toppingSizeIds[toppingId]]
          : [];
        const priceSizes = Object.keys(def.price || {});
        const sizeSet = new Set([...productSizes, ...priceSizes]);

        if (!sizeSet.size) return;

        const priceMap = {};
        const priceWithoutTaxMap = {};
        const anyPrice = Object.values(def.price || {})[0];

        sizeSet.forEach((sizeId) => {
          priceMap[sizeId] = def.price?.[sizeId] ?? anyPrice ?? "0.0";
          priceWithoutTaxMap[sizeId] =
            def.priceWithoutTax?.[sizeId] ?? priceMap[sizeId];
        });

        const toppingsSizeSkuIdMap = {};
        sizeSet.forEach((sizeId) => {
          toppingsSizeSkuIdMap[sizeId] = `${toppingId}#${sizeId}`;
        });

        toppingItems.push({
          price: priceMap,
          toppingId,
          description: def.description,
          name: def.name,
          images: def.images,
          type: def.type,
          custAttrField: def.custAttrField || "",
          priceWithoutTax: priceWithoutTaxMap,
          toppingsSizeSkuIdMap,
          typeName: def.typeName,
        });
      });

      return toppingItems;
    },
  },
};
</script>
